/**
 * Smart Palette Generator
 *
 * Generates a harmonious color theme from extracted logo colors.
 * Ensures all generated colors are appropriate for their use case:
 * - Brand colors (primary/secondary/accent) are vibrant and visible
 * - Text colors have proper contrast against their backgrounds
 * - Background colors are light but tinted
 */

import type { ColorTheme } from '../types';

// Simple interface - we only need hex and area, HSL is calculated
export interface ExtractedColor {
  hex: string;
  area: number;
}

/**
 * Convert hex to HSL for color analysis
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to hex
 */
function hslToHex(h: number, s: number, l: number): string {
  // Normalize h to 0-360
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (0 <= h && h < 60) { r = c; g = x; b = 0; }
  else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
  else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
  else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
  else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
  else if (300 <= h && h < 360) { r = c; g = 0; b = x; }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Calculate relative luminance for contrast calculations (WCAG formula)
 */
function getLuminance(hex: string): number {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 0;

  const rgb = [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255,
  ].map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));

  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

/**
 * Calculate contrast ratio between two colors (WCAG)
 */
function getContrastRatio(hex1: string, hex2: string): number {
  const l1 = getLuminance(hex1);
  const l2 = getLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if a color is too close to white or pure gray to be a brand color
 * Note: Dark colors including black ARE allowed - many brands use black
 */
function isUsableAsBrandColor(hex: string): boolean {
  const { s, l } = hexToHsl(hex);

  // Too light (near white) - can't see on white backgrounds
  if (l > 85) return false;

  // Gray colors need SOME saturation to be brand colors, unless they're very dark
  // Dark grays (like black) are fine as brand colors
  if (s < 15 && l > 30) return false;

  return true;
}

/**
 * Score a color for suitability as a primary brand color
 */
function scoreColorForPrimary(hex: string, area: number): number {
  const { s, l } = hexToHsl(hex);

  let score = 0;

  // Prefer saturated colors (vibrant)
  score += s * 2;

  // Prefer mid-lightness (40-60% is ideal)
  const lightnessScore = 100 - Math.abs(l - 50) * 2;
  score += lightnessScore;

  // Bonus for prominent colors (larger area)
  score += area * 30;

  // Penalty for very light or very dark
  if (l > 75) score -= 50;
  if (l < 25) score -= 50;

  return score;
}

/**
 * Ensure a brand color is usable as primary
 * - Dark colors (including black) are kept as-is
 * - Light colors are darkened to be visible on white
 * - Mid-range colors get saturation boost if needed
 */
function ensureVibrantBrandColor(hex: string): string {
  const { h, s, l } = hexToHsl(hex);

  // Very dark colors (black, near-black): keep them exactly as they are
  // Black is a valid brand color used by many companies
  if (l < 20) {
    return hex;
  }

  // For lighter colors, ensure minimum saturation and cap lightness
  const targetSaturation = Math.max(s, 35);
  let targetLightness = l;
  if (l > 65) targetLightness = 60;

  const adjusted = hslToHex(h, targetSaturation, targetLightness);

  // Also ensure it's visible on white (helps with yellows and light colors)
  return ensureVisibleOnWhite(adjusted);
}

/**
 * Ensure a color has adequate contrast against white (3:1 minimum for UI elements)
 * Darkens the color if needed while preserving hue
 */
function ensureVisibleOnWhite(hex: string): string {
  const minContrast = 3; // 3:1 for large text/UI elements
  let contrast = getContrastRatio(hex, '#ffffff');

  if (contrast >= minContrast) {
    return hex;
  }

  // Need to darken - reduce lightness until we hit the contrast target
  const { h, s, l } = hexToHsl(hex);
  let targetLightness = l;

  while (contrast < minContrast && targetLightness > 10) {
    targetLightness -= 5;
    const adjusted = hslToHex(h, s, targetLightness);
    contrast = getContrastRatio(adjusted, '#ffffff');
    if (contrast >= minContrast) {
      return adjusted;
    }
  }

  // If still not enough contrast, also boost saturation
  return hslToHex(h, Math.min(s + 20, 100), targetLightness);
}

/**
 * Create a secondary color that's visible but complementary
 */
function createSecondary(primaryHex: string): string {
  const { h, s, l } = hexToHsl(primaryHex);

  // Shift hue slightly for variety
  const newHue = (h + 20) % 360;

  // Secondary should be slightly lighter/softer but still visible
  // Ensure saturation stays reasonable (at least 30%)
  const newSaturation = Math.max(s - 10, 30);

  // Start with a reasonable lightness
  const baseLightness = Math.min(Math.max(l + 8, 35), 55);

  const baseColor = hslToHex(newHue, newSaturation, baseLightness);

  // Ensure it's visible on white (yellows and light colors get darkened)
  return ensureVisibleOnWhite(baseColor);
}

/**
 * Create an accent color that pops but stays harmonious
 */
function createAccent(primaryHex: string): string {
  const { h, s, l } = hexToHsl(primaryHex);

  // Use split-analogous (-60 degrees) for a harmonious accent
  // For blue → teal/cyan, for orange → magenta, for green → cyan
  // This creates pleasing combinations without jarring contrasts
  const newHue = (h - 60 + 360) % 360;

  // Accent should be vibrant (high saturation)
  const newSaturation = Math.max(s, 50);

  // Mid-lightness for visibility
  const baseLightness = Math.min(Math.max(l, 40), 50);

  const baseColor = hslToHex(newHue, newSaturation, baseLightness);

  // Ensure it's visible on white
  return ensureVisibleOnWhite(baseColor);
}

/**
 * Create a light background tint from a color
 */
function createLightBackground(hex: string): string {
  const { h, s } = hexToHsl(hex);
  // Very light (95-97%) with subtle saturation for tint
  return hslToHex(h, Math.max(s * 0.4, 15), 96);
}

/**
 * Create a text color with good contrast against a background
 */
function createTextColor(bgHex: string, baseHex: string): string {
  const { h, s } = hexToHsl(baseHex);

  // Start with a dark version of the base color
  let textColor = hslToHex(h, Math.min(s * 0.9, 40), 20);

  // Check contrast and adjust if needed
  let contrast = getContrastRatio(textColor, bgHex);
  let lightness = 20;

  // Ensure WCAG AA contrast (4.5:1 minimum)
  while (contrast < 4.5 && lightness > 5) {
    lightness -= 5;
    textColor = hslToHex(h, Math.min(s * 0.9, 40), lightness);
    contrast = getContrastRatio(textColor, bgHex);
  }

  return textColor;
}

/**
 * Create a border color - subtle but visible
 */
function createBorderColor(primaryHex: string): string {
  const { h, s } = hexToHsl(primaryHex);
  // Light but with enough color to be visible
  return hslToHex(h, Math.max(s * 0.3, 10), 85);
}

/**
 * Generate a full color theme from extracted colors
 */
export function generatePaletteFromColors(colors: ExtractedColor[]): ColorTheme | null {
  // Filter to usable brand colors only
  const usableColors = colors.filter((c) => isUsableAsBrandColor(c.hex));

  if (usableColors.length === 0) {
    // If no usable colors, try to salvage the best one and boost it
    if (colors.length > 0) {
      const sorted = [...colors].sort((a, b) =>
        scoreColorForPrimary(b.hex, b.area) - scoreColorForPrimary(a.hex, a.area)
      );
      const boosted = ensureVibrantBrandColor(sorted[0].hex);
      return generateThemeFromPrimary(boosted);
    }
    return null;
  }

  // Score and sort to find the best primary color
  const scored = usableColors.map((c) => ({
    ...c,
    score: scoreColorForPrimary(c.hex, c.area),
  }));
  scored.sort((a, b) => b.score - a.score);

  // Ensure the primary is vibrant enough
  const primary = ensureVibrantBrandColor(scored[0].hex);

  // Try to find a good secondary from extracted colors
  let secondary: string;
  if (scored.length > 1) {
    const candidateSecondary = ensureVibrantBrandColor(scored[1].hex);
    const { h: h1 } = hexToHsl(primary);
    const { h: h2 } = hexToHsl(candidateSecondary);
    const hueDiff = Math.abs(h1 - h2);

    // Use extracted color if it's different enough from primary
    if (hueDiff > 25 || hueDiff < 335) {
      secondary = ensureVisibleOnWhite(candidateSecondary);
    } else {
      secondary = createSecondary(primary);
    }
  } else {
    secondary = createSecondary(primary);
  }

  // Try to find an accent from extracted colors, or generate one
  let accent: string;
  if (scored.length > 2) {
    accent = ensureVisibleOnWhite(ensureVibrantBrandColor(scored[2].hex));
  } else {
    accent = createAccent(primary);
  }

  return generateThemeFromColors(primary, secondary, accent);
}

/**
 * Generate full theme from just a primary color (exported for explicit selection)
 */
export function generateThemeFromPrimary(primaryHex: string): ColorTheme {
  // Ensure the primary is vibrant enough
  const primary = ensureVibrantBrandColor(primaryHex);
  const secondary = createSecondary(primary);
  const accent = createAccent(primary);
  return generateThemeFromColors(primary, secondary, accent);
}

/**
 * Generate the full theme with proper contrast-checked colors
 */
function generateThemeFromColors(primary: string, secondary: string, accent: string): ColorTheme {
  // Create backgrounds
  const headerBg = createLightBackground(primary);
  const tableHeaderBg = createLightBackground(primary);

  // Create text colors with proper contrast
  const headerText = createTextColor(headerBg, primary);
  const tableHeaderText = createTextColor(tableHeaderBg, primary);
  const total = createTextColor('#ffffff', primary);

  // Create borders and dividers
  const tableBorder = createBorderColor(primary);
  const divider = createBorderColor(primary);

  const theme: ColorTheme = {
    primary,
    secondary,
    accent,
    headerBg,
    headerText,
    tableBorder,
    tableHeaderBg,
    tableHeaderText,
    tableAltRow: '#fafafa',
    divider,
    total,
  };

  return theme;
}

/**
 * Validate extracted colors and return usable ones with analysis
 */
export function analyzeExtractedColors(colors: ExtractedColor[]): {
  usableColors: ExtractedColor[];
  hasEnoughColors: boolean;
  primaryCandidate: ExtractedColor | null;
} {
  const usableColors = colors.filter((c) => isUsableAsBrandColor(c.hex));

  const scored = usableColors.map((c) => ({
    ...c,
    score: scoreColorForPrimary(c.hex, c.area),
  }));
  scored.sort((a, b) => b.score - a.score);

  return {
    usableColors,
    hasEnoughColors: usableColors.length > 0,
    primaryCandidate: scored[0] || null,
  };
}
